import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ExternalLink, Plus, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { scrollToSection } from "../hooks/useLenis";

const LaptopFrame = ({ src, alt, url }) => (
    <div className="relative">
        <div className="absolute -inset-8 rounded-full bg-[#0066FF]/12 blur-3xl" aria-hidden="true" />
        <div className="relative rounded-2xl border border-white/15 bg-gradient-to-b from-slate-800 to-slate-900 p-2 shadow-[0_40px_90px_rgba(0,0,0,0.6)]">
            <div className="flex items-center gap-1.5 px-2 py-1.5">
                <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
                <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
                <span className="h-2 w-2 rounded-full bg-[#28C840]" />
                <span className="ml-2 flex-1 rounded-full bg-white/5 px-3 py-0.5 font-mono text-[9px] text-slate-400">
                    {url}
                </span>
            </div>
            <div className="group/img relative aspect-[16/10] overflow-hidden rounded-lg">
                <img
                    src={src}
                    alt={alt}
                    className="h-full w-full object-cover object-top transition-transform duration-[1400ms] ease-out group-hover/img:scale-[1.04]"
                    loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#05070B]/40 to-transparent opacity-0 transition-opacity duration-500 group-hover/img:opacity-100" />
            </div>
        </div>
        <div className="mx-auto h-3 w-[110%] -translate-x-[5%] rounded-b-2xl bg-gradient-to-b from-slate-700 to-slate-900" />
    </div>
);

const PhoneFrame = ({ src, alt }) => (
    <div className="relative mx-auto w-36 sm:w-44">
        <div className="absolute -inset-6 rounded-full bg-[#0066FF]/15 blur-2xl" aria-hidden="true" />
        <div className="relative overflow-hidden rounded-[1.6rem] border-[3px] border-slate-700 bg-slate-900 shadow-[0_30px_60px_rgba(0,0,0,0.6)]">
            <div className="absolute left-1/2 top-1.5 z-10 h-3.5 w-14 -translate-x-1/2 rounded-full bg-slate-900" aria-hidden="true" />
            <img src={src} alt={alt} className="aspect-[9/16] w-full object-cover object-top" loading="lazy" />
        </div>
    </div>
);

export const Portfolio = () => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const [isLg, setIsLg] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 1024px)");
        const fn = () => setIsLg(mq.matches);
        fn();
        mq.addEventListener("change", fn);
        return () => mq.removeEventListener("change", fn);
    }, []);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
    const x = useTransform(scrollYProgress, [0, 1], ["0%", "-82%"]);
    const trackStyle = reduce || !isLg ? {} : { x };

    return (
        <section id="work" ref={ref} className="relative lg:h-[430vh]" data-testid="portfolio-section">
            <div className="relative overflow-hidden py-24 md:py-32 lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-center lg:py-0">
                <div className="bg-grid-faint absolute inset-0 opacity-60" aria-hidden="true" />

                <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <Reveal>
                                <Eyebrow>Selected Work</Eyebrow>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                                    DON&apos;T JUST TAKE<br />OUR <span className="text-electric-grad">WORD</span> FOR IT.
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.2} className="hidden lg:block">
                            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                Scroll to explore <ArrowRight className="h-3.5 w-3.5 text-sky-400" />
                            </p>
                        </Reveal>
                    </div>
                </div>

                <motion.div
                    style={trackStyle}
                    className="relative mt-14 flex flex-col gap-10 px-4 will-change-transform sm:px-6 lg:mt-16 lg:w-max lg:flex-row lg:gap-8 lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))] lg:pr-8"
                >
                    <article data-testid="work-panel-0" className="glass relative rounded-3xl p-6 sm:p-8 lg:w-[56vw] lg:max-w-[860px]">
                        <span className="sticker absolute -top-3 left-6 z-10">Live Site ★</span>
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                            <p className="font-mono text-xs tracking-[0.3em] text-slate-500">01 — FEATURED PROJECT</p>
                            <div className="flex flex-wrap gap-2">
                                {["Ministry", "Web Design", "Media"].map((t) => (
                                    <span key={t} className="rounded-full border border-[#0A0F1D]/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#0066FF]">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <LaptopFrame
                            src="/ttm-mid.webp"
                            alt="Terrell Turner Ministries website homepage on desktop — designed by Footman Digital Solutions"
                            url="terrellturnerministries.com"
                        />
                        <div className="mt-7 flex flex-wrap items-end justify-between gap-5">
                            <div>
                                <h3 className="font-display text-2xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-3xl">
                                    TERRELL TURNER MINISTRIES
                                </h3>
                                <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                                    A cinematic digital home for a prophetic ministry — events, media, books,
                                    coaching, and community in one premium experience.
                                </p>
                            </div>
                            <a
                                data-testid="portfolio-live-link"
                                href="https://terrellturnerministries.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-2 rounded-full glass px-6 py-3 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-[#0A0F1D] transition-[border-color,box-shadow] duration-300 hover:border-[#0066FF]/60 hover:shadow-[0_0_30px_rgba(0,102,255,0.25)]"
                            >
                                View Live Website
                                <ExternalLink className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    </article>

                    <article data-testid="work-panel-1" className="glass relative rounded-3xl p-6 sm:p-8 lg:w-[56vw] lg:max-w-[860px]">
                        <span className="sticker absolute -top-3 left-6 z-10 rotate-2">Live Site ★</span>
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                            <p className="font-mono text-xs tracking-[0.3em] text-slate-500">02 — LIVE PROJECT</p>
                            <div className="flex flex-wrap gap-2">
                                {["Business", "Compliance", "Web Design"].map((t) => (
                                    <span key={t} className="rounded-full border border-[#0A0F1D]/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#0066FF]">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <LaptopFrame
                            src="/nm-desktop.webp"
                            alt="Next Mile Compliance website homepage — designed by Footman Digital Solutions"
                            url="nextmilecompliance.com"
                        />
                        <div className="mt-7 flex flex-wrap items-end justify-between gap-5">
                            <div>
                                <h3 className="font-display text-2xl tracking-tight text-[#0A0F1D] sm:text-3xl">
                                    NEXT MILE COMPLIANCE
                                </h3>
                                <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-600">
                                    A bold, credible web presence for a trucking compliance and dispatch
                                    company — services, training, and safety audits made easy to navigate.
                                </p>
                            </div>
                            <a
                                data-testid="portfolio-live-link-2"
                                href="https://nextmilecompliance.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-2 rounded-full glass px-6 py-3 font-display text-[11px] uppercase tracking-[0.2em] text-[#0A0F1D] transition-[border-color,box-shadow] duration-300 hover:border-[#0066FF]/60 hover:shadow-[0_0_30px_rgba(0,102,255,0.25)]"
                            >
                                View Live Website
                                <ExternalLink className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </a>
                        </div>
                    </article>

                    <article data-testid="work-panel-2" className="glass flex flex-col rounded-3xl p-6 sm:p-8 lg:w-[32vw] lg:min-w-[360px]">
                        <p className="font-mono text-xs tracking-[0.3em] text-slate-500">03 — MOBILE EXPERIENCE</p>
                        <div className="flex flex-1 items-center py-8">
                            <div className={reduce ? "w-full" : "w-full animate-float-soft"}>
                                <PhoneFrame
                                    src="/ttm-mobile.webp"
                                    alt="Terrell Turner Ministries website on a phone — designed mobile-first by Footman Digital Solutions"
                                />
                            </div>
                        </div>
                        <h3 className="font-display text-xl font-extrabold tracking-tight text-white sm:text-2xl">
                            DESIGNED FOR THE PHONE IN THEIR HAND.
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">
                            Most visitors will meet you on mobile first. Every build is engineered phone-up,
                            then scaled to the big screen.
                        </p>
                    </article>

                    <button
                        data-testid="portfolio-next-project"
                        onClick={() => scrollToSection("contact")}
                        className="group flex flex-col items-start justify-between gap-10 rounded-3xl border border-dashed border-[#0A0F1D]/15 p-6 text-left transition-[border-color,background-color] duration-500 hover:border-[#0066FF]/50 hover:bg-white/[0.03] sm:p-8 lg:w-[32vw] lg:min-w-[360px]"
                    >
                        <p className="font-mono text-xs tracking-[0.3em] text-slate-500">04 — NEXT PROJECT</p>
                        <div>
                            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#0A0F1D]/15 text-slate-500 transition-[background-color,color,transform] duration-500 group-hover:rotate-90 group-hover:bg-[#0066FF] group-hover:text-white">
                                <Plus className="h-6 w-6" />
                            </span>
                            <span className="mt-6 block font-display text-2xl font-extrabold tracking-tight text-slate-500 transition-colors duration-300 group-hover:text-[#0A0F1D] sm:text-3xl">
                                YOUR PROJECT<br />COULD BE NEXT<span className="text-electric-grad">.</span>
                            </span>
                            <span className="mt-3 block max-w-xs text-sm leading-relaxed text-slate-500">
                                Tell me the vision. I&apos;ll turn it into something worth scrolling through.
                            </span>
                        </div>
                        <span className="inline-flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.2em] text-sky-400">
                            Start Your Project
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                    </button>
                </motion.div>

                <div className="absolute bottom-10 left-1/2 hidden w-56 -translate-x-1/2 lg:block" aria-hidden="true">
                    <div className="h-px w-full bg-[#0A0F1D]/10">
                        <motion.div style={{ scaleX: scrollYProgress }} className="h-full origin-left bg-gradient-to-r from-sky-400 to-[#0066FF]" />
                    </div>
                </div>
            </div>
        </section>
    );
};
